/**
 * FundingDetailPage
 * Displays detail of a single funding instruction.
 * If PENDING_FUNDING: shows confirmation form for SPEI reference.
 * If CONFIRMED: shows confirmation details.
 * Route: /funding/instructions/:fundingId
 */

import { useParams } from 'react-router-dom';
import { AlertTriangle, RefreshCw, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { HelpButton } from '@/features/help';
import { useFundingDetail } from '@/features/funding/hooks';
import { ConfirmFundingForm } from '@/features/funding/components/ConfirmFundingForm';
import { formatCurrency } from '@/utils/formatters';
import { ROUTES } from '@/constants';

// ─── Constants ───────────────────────────────────────────────────────────────

const BREADCRUMBS = [
  { label: 'Inicio', path: ROUTES.DASHBOARD },
  { label: 'Fondeo', path: ROUTES.FUNDING_INSTRUCTIONS },
  { label: 'Instrucciones', path: ROUTES.FUNDING_INSTRUCTIONS },
  { label: 'Detalle', path: '#' },
];

const WINDOW_LABELS: Record<string, string> = {
  MORNING: 'Ventana Matutina',
  AFTERNOON: 'Ventana Vespertina',
};

type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info';

function getStatusVariant(status: string): BadgeVariant {
  switch (status) {
    case 'PENDING_FUNDING':
      return 'warning';
    case 'INCLUDED':
      return 'info';
    case 'CONFIRMED':
      return 'success';
    default:
      return 'default';
  }
}

function getStatusLabel(status: string): string {
  switch (status) {
    case 'PENDING_FUNDING':
      return 'Pendiente';
    case 'INCLUDED':
      return 'Incluido';
    case 'CONFIRMED':
      return 'Confirmado';
    default:
      return status;
  }
}

function formatDateFull(dateStr: string | null): string {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat('es-MX', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

// ─── Skeleton ────────────────────────────────────────────────────────────────

function DetailSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton variant="rectangular" height={200} />
      <Skeleton variant="rectangular" height={150} />
    </div>
  );
}

// ─── Error State ─────────────────────────────────────────────────────────────

function DetailError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card px-6 py-16">
      <AlertTriangle size={48} className="mb-4 text-muted-foreground" />
      <h3 className="text-lg font-semibold text-foreground">
        Error al cargar el detalle de la instruccion
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Hubo un error al obtener los datos. Intenta de nuevo.
      </p>
      <Button className="mt-4" onClick={onRetry}>
        <RefreshCw size={16} />
        Reintentar
      </Button>
    </div>
  );
}

// ─── Detail Row Helper ───────────────────────────────────────────────────────

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-4">
      <span className="min-w-[180px] text-sm font-medium text-muted-foreground">{label}</span>
      <span className="text-sm text-foreground">{children}</span>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export function FundingDetailPage() {
  const { fundingId } = useParams<{ fundingId: string }>();
  const navigate = useNavigate();
  const { instruction, isLoading, isError, refetch } = useFundingDetail(fundingId);

  if (isLoading) {
    return (
      <div>
        <PageHeader
          title="Detalle de Instruccion"
          breadcrumbs={BREADCRUMBS}
        />
        <DetailSkeleton />
      </div>
    );
  }

  if (isError || !instruction) {
    return (
      <div>
        <PageHeader
          title="Detalle de Instruccion"
          breadcrumbs={BREADCRUMBS}
        />
        <DetailError onRetry={() => refetch()} />
      </div>
    );
  }

  const isPending = instruction.status === 'PENDING_FUNDING';
  const isConfirmed = instruction.status === 'CONFIRMED';

  return (
    <div>
      <PageHeader
        title="Detalle de Instruccion"
        breadcrumbs={BREADCRUMBS}
        actions={
          <div className="flex items-center gap-2">
            <HelpButton pageKey="funding-detail" />
            <Button
              variant="outline"
              onClick={() => navigate(ROUTES.FUNDING_INSTRUCTIONS)}
            >
              <ArrowLeft size={16} />
              Regresar
            </Button>
          </div>
        }
      />

      {/* Instruction Info Card */}
      <Card title="Informacion de la Instruccion" className="mb-6">
        <div className="space-y-3">
          <DetailRow label="ID Fondeo">
            <span className="font-mono">{instruction.funding_id}</span>
          </DetailRow>
          <DetailRow label="ID Credito">
            <span className="font-mono">{instruction.credit_id}</span>
          </DetailRow>
          <DetailRow label="Partner ID">
            <span className="font-mono">{instruction.partner_id}</span>
          </DetailRow>
          <DetailRow label="Directory ID">
            <span className="font-mono">{instruction.directory_id}</span>
          </DetailRow>
          <DetailRow label="Ventana">
            {WINDOW_LABELS[instruction.funding_window] ?? instruction.funding_window}
          </DetailRow>
          <DetailRow label="Monto">
            <span className="text-lg font-semibold tabular-nums">
              {formatCurrency(instruction.amount_mxn, 'MXN', 'es-MX')}
            </span>
          </DetailRow>
          <DetailRow label="Estado">
            <Badge variant={getStatusVariant(instruction.status)}>
              {getStatusLabel(instruction.status)}
            </Badge>
          </DetailRow>
          <DetailRow label="Creado">
            {formatDateFull(instruction.created_at)}
          </DetailRow>
          <DetailRow label="Actualizado">
            {formatDateFull(instruction.updated_at)}
          </DetailRow>
        </div>
      </Card>

      {/* Pending: show confirmation form */}
      {isPending && (
        <Card title="Confirmar Transferencia">
          <p className="mb-4 text-sm text-muted-foreground">
            Ingresa la referencia bancaria SPEI para confirmar que la transferencia fue realizada.
          </p>
          <ConfirmFundingForm fundingId={instruction.funding_id} />
        </Card>
      )}

      {/* Confirmed: show confirmation details */}
      {isConfirmed && (
        <Card title="Datos de Confirmacion">
          <div className="space-y-3">
            <DetailRow label="Estado">
              <Badge variant="success">Confirmado</Badge>
            </DetailRow>
            <DetailRow label="Fecha de confirmacion">
              {formatDateFull(instruction.updated_at)}
            </DetailRow>
          </div>
        </Card>
      )}
    </div>
  );
}
